import React, { useEffect, useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Heart, ArrowLeft, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import FavoriteButton from '../components/FavoriteButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TutorialPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, setShowAuthModal } = useContext(AuthContext);
  const [tutorial, setTutorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    fetchTutorial();
  }, [id]);

  const fetchTutorial = async () => {
    try {
      const response = await axios.get(`${API}/tutorials/${id}`);
      setTutorial(response.data);
    } catch (error) {
      console.error('Failed to fetch tutorial', error);
      toast.error('Tutoriel introuvable');
      navigate('/browse');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!token) {
      setShowAuthModal(true);
      return;
    }

    try {
      await axios.post(
        `${API}/tutorials/${id}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLiked(true);
      setTutorial({ ...tutorial, likes: tutorial.likes + 1 });
      toast.success('Ajouté à vos favoris!');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout aux favoris');
    }
  };

  const getVideoEmbedUrl = (url) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('youtu.be/')[1]?.split('?')[0]
        : url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('vimeo.com')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
  };

  const canAccess = user && user.has_subscription;
  const isLocked = tutorial?.is_premium && !canAccess;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (!tutorial) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pb-24 md:pb-12"
      data-testid="tutorial-page"
    >
      {/* Back Button */}
      <div className="container mx-auto px-6 py-6">
        <button
          onClick={() => navigate('/browse')}
          data-testid="tutorial-back-btn"
          className="flex items-center text-charcoal hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span className="uppercase tracking-wide text-sm">Retour</span>
        </button>
      </div>

      {/* Video Section */}
      <section className="container mx-auto px-6 mb-12">
        <div className="max-w-5xl mx-auto">
          {isLocked ? (
            <div className="relative bg-obsidian aspect-video flex items-center justify-center border gold-border" data-testid="locked-video">
              <div className="text-center space-y-6 p-8">
                <Lock className="w-16 h-16 text-primary mx-auto" />
                <div>
                  <h3 className="font-serif text-3xl text-white mb-3">Contenu Premium</h3>
                  <p className="text-silk mb-6">Abonnez-vous pour accéder à ce tutoriel exclusif</p>
                  <Link
                    to="/subscription"
                    data-testid="locked-subscribe-btn"
                    className="inline-block bg-secondary text-white hover:bg-secondary/90 px-8 py-4 uppercase tracking-widest font-serif text-sm transition-all duration-500"
                  >
                    S'abonner maintenant - 9,29$
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative aspect-video bg-obsidian" data-testid="video-player">
              <iframe
                src={getVideoEmbedUrl(tutorial.video_url)}
                title={tutorial.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>
      </section>

      {/* Tutorial Info */}
      <section className="container mx-auto px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between mb-8">
            <div className="flex-1">
              <p className="font-accent text-sm uppercase tracking-wider text-primary mb-3">
                {tutorial.category}
                {tutorial.subcategory && ` / ${tutorial.subcategory}`}
              </p>
              <h1 className="font-serif text-4xl md:text-5xl text-charcoal tracking-tighter mb-4">
                {tutorial.title}
              </h1>
              <p className="text-lg text-taupe leading-relaxed">{tutorial.description}</p>
            </div>

            <div className="ml-6 flex-shrink-0 flex gap-3">
              <FavoriteButton tutorialId={tutorial.id} size="large" />
              <button
                onClick={handleLike}
                disabled={liked}
                data-testid="tutorial-like-btn"
                className={`p-4 border gold-border hover:bg-primary hover:text-white transition-all duration-300 ${
                  liked ? 'bg-primary text-white' : 'bg-transparent text-charcoal'
                }`}
              >
                <Heart className={`w-6 h-6 ${liked ? 'fill-current' : ''}`} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-8 pt-8 border-t gold-border">
            <div>
              <p className="text-sm text-taupe uppercase tracking-wide mb-1">J'aime</p>
              <p className="font-serif text-2xl text-primary" data-testid="tutorial-likes-count">{tutorial.likes}</p>
            </div>
            <div>
              <p className="text-sm text-taupe uppercase tracking-wide mb-1">Catégorie</p>
              <p className="font-serif text-2xl text-charcoal">{tutorial.category}</p>
            </div>
            {tutorial.is_premium && (
              <div>
                <p className="text-sm text-taupe uppercase tracking-wide mb-1">Type</p>
                <div className="bg-primary px-4 py-2">
                  <span className="font-accent text-xs text-white uppercase tracking-wider">Premium</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default TutorialPage;